import React, { useEffect, useState } from "react";
import { Badge, Card, Flex, Tag, Typography } from "antd";
import { CalendarOutlined, ContactsOutlined, FileTextOutlined, MessageOutlined } from "@ant-design/icons";
import { useGetIdentity, useGo } from "@refinedev/core";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { useOperatorContacts } from "@/hooks/useOperatorContacts";
import { axiosInstance } from "@/utilities";
import { API_URL_QUOTES, API_URL_ORDERS } from "@/services/XCONS_ENV";
import { conversationsApi, isConversationsConfigured, type ConversationListItem } from "@/services/conversations.api";
import { historyNotesApi, type HistoryModule } from "@/services/history.api";
import { MailOutlined, ShopOutlined, UserOutlined } from "@ant-design/icons";
import { WhatsAppMetaIcon, WhatsAppQrIcon } from "@/components/icon/WhatsAppChannelIcons";
import { SellerIcon } from "@/components/icon/SellerIcon";
import { BoxCloseIcon } from "@/components/icon";
import type { User } from "@/types/schema/schema.types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { isCloudApi, isWhatsappFamily } from "@/services/channel-types";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { Text } = Typography;

// ── Tipos ─────────────────────────────────────────────────────────────────────

type ActivityRow = {
  module: HistoryModule;
  entityId: string;
  label: string;
  scheduledFor: string;
  content?: string;
  authorName?: string;
};

type ConvItem = ConversationListItem & {
  store_name?: string;
  owner_name?: string;
  seller_name?: string;
  seller_person?: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const relTime = (iso: string | null) => {
  if (!iso) return "";
  const d = dayjs(iso);
  const isToday = d.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
  return isToday ? d.format("HH:mm") : d.format("DD/MM");
};

const ChannelBadge = ({ type, name }: { type: string; name: string }) => {
  const icon =
    isCloudApi(type)       ? <WhatsAppMetaIcon style={{ fontSize: 12 }} /> :
    isWhatsappFamily(type) ? <WhatsAppQrIcon style={{ fontSize: 12 }} /> :
    type === "email"    ? <MailOutlined style={{ fontSize: 12, color: "#1677ff" }} /> :
                          <MessageOutlined style={{ fontSize: 12 }} />;
  return (
    <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
      {icon}
      <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{name}</Text>
    </Flex>
  );
};

const ACTIVITY_MODULE: Record<string, { icon: JSX.Element; label: string }> = {
  contact:      { icon: <ContactsOutlined style={{ fontSize: 12 }} />, label: "Contacto" },
  quote:        { icon: <FileTextOutlined style={{ fontSize: 12 }} />, label: "Presupuesto" },
  order:        { icon: <BoxCloseIcon style={{ width: 12, height: 12 }} />, label: "Pedido" },
  conversation: { icon: <MessageOutlined style={{ fontSize: 12 }} />,  label: "Conversación" },
};

// ── Subcomponentes ────────────────────────────────────────────────────────────

const SectionHeader = ({
  icon,
  label,
  count,
  loading,
  onLabelClick,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  loading: boolean;
  onLabelClick?: () => void;
}) => (
  <Flex align="center" justify="space-between" style={{ width: "100%" }}>
    <Flex align="center" gap={8}
      onClick={onLabelClick}
      style={onLabelClick ? { cursor: "pointer" } : undefined}
    >
      {icon}
      <Text strong style={{ fontSize: 14, fontWeight: 600, color: onLabelClick ? "#15744b" : undefined }}>{label}</Text>
    </Flex>
    <Text style={{ fontSize: 14, color: "rgba(0,0,0,0.45)", opacity: loading ? 0.4 : 1 }}>
      {count || ""}
    </Text>
  </Flex>
);

const EmptyState = ({ text }: { text: string }) => (
  <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.35)", display: "block", padding: "6px 0" }}>
    {text}
  </Text>
);

// ── Conversaciones abiertas ───────────────────────────────────────────────────

const ConversacionesAbiertas = ({
  conversations,
  loading,
  error,
}: {
  conversations: ConvItem[];
  loading: boolean;
  error?: boolean;
}) => {
  if (loading)
    return <Text style={{ fontSize: 12, opacity: 0.4 }}>Cargando...</Text>;
  // "No hay conversaciones" y "no las pudimos traer" son cosas distintas: si se dibujan igual, el
  // operador cree que su bandeja está vacía cuando en realidad no se pudo consultar.
  if (error) return <EmptyState text="No se pudieron cargar las conversaciones" />;
  if (!conversations.length)
    return <EmptyState text="Sin conversaciones abiertas" />;

  const MetaRow = ({ icon, label, person }: { icon: React.ReactNode; label?: string; person?: string }) => (
    <Flex gap={8} align="center">
      <Flex gap={2} align="center" style={{ flexShrink: 0 }}>
        <span style={{ display: "inline-flex", alignItems: "center", color: "rgba(0,0,0,0.45)" }}>{icon}</span>
        {label && <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.65)", lineHeight: "16px" }}>{label}</Text>}
      </Flex>
      {person && (
        <Flex gap={2} align="center">
          <UserOutlined style={{ fontSize: 10, color: "#15744b" }} />
          <Text style={{ fontSize: 10, color: "#15744b", fontWeight: 500, lineHeight: "16px" }}>{person}</Text>
        </Flex>
      )}
    </Flex>
  );

  return (
    <>
      {conversations.map((conv, i) => {
        const unread = conv.unread_count > 0;
        return (
          <div
            key={conv.conversation_id}
            onClick={() => window.open(`/conversations?open=${conv.conversation_id}`, "_blank")}
            style={{
              padding: "8px 0",
              borderBottom: i === conversations.length - 1 ? "none" : "1px solid #f5f5f5",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {/* Fila 1: nombre | canal (icono + nombre gris) */}
            <Flex justify="space-between" align="center" gap={8}>
              <Text style={{ fontSize: 14, fontWeight: 600, minWidth: 0 }} ellipsis>
                {conv.contact_name || "Sin nombre"}
              </Text>
              <ChannelBadge type={conv.channel_type} name={conv.channel_name} />
            </Flex>

            {/* Fila 2: meta (store+owner / seller+person) | timestamp + badge */}
            <Flex justify="space-between" align="flex-start" gap={8}>
              <Flex vertical gap={2} style={{ minWidth: 0, flex: 1 }}>
                <MetaRow
                  icon={<ShopOutlined style={{ fontSize: 12 }} />}
                  label={conv.store_name ?? conv.store_code}
                  person={conv.owner_name}
                />
                {(conv.seller_name || conv.seller_person) && (
                  <MetaRow
                    icon={<SellerIcon style={{ fontSize: 12 }} />}
                    label={conv.seller_name}
                    person={conv.seller_person}
                  />
                )}
              </Flex>
              <Flex vertical align="flex-end" justify="space-between" style={{ flexShrink: 0, height: 40 }}>
                <Text style={{ fontSize: 12, fontWeight: 600, color: "#15744b", lineHeight: "20px" }}>
                  {relTime(conv.last_message_at)}
                </Text>
                {unread
                  ? <Badge count={conv.unread_count} size="small" color="#15744b" />
                  : <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#15744b" }} />
                }
              </Flex>
            </Flex>

            {/* Fila 3: preview del último mensaje */}
            <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)", display: "block" }} ellipsis>
              {conv.last_message_sender === "operator" ? "Vos: " : ""}{conv.last_message_text ?? "—"}
            </Text>
          </div>
        );
      })}
    </>
  );
};

const NOTE_BG = "#fffbe6";
const NOTE_BORDER = "#ffe58f";
const BRAND = "#15744b";
const MENTION_RE = /@\[([^\]]+)\]\(\d+\)/g;

function renderNoteContent(content: string): (string | JSX.Element)[] | string {
  const parts: (string | JSX.Element)[] = [];
  let last = 0;
  for (const m of content.matchAll(MENTION_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push(content.slice(last, idx));
    parts.push(
      <span key={idx} style={{ color: BRAND, fontStyle: "italic", fontWeight: 600 }}>
        @{m[1]}
      </span>,
    );
    last = idx + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts.length ? parts : content;
}

// ── Próximas actividades ──────────────────────────────────────────────────────

const ProximasActividades = ({
  activities,
  loading,
  error,
  onNavigate,
}: {
  activities: ActivityRow[];
  loading: boolean;
  error?: boolean;
  onNavigate: (module: HistoryModule, entityId: string) => void;
}) => {
  if (loading)
    return <Text style={{ fontSize: 12, opacity: 0.4 }}>Cargando...</Text>;
  if (error) return <EmptyState text="No se pudieron cargar las actividades" />;
  if (!activities.length)
    return <EmptyState text="Sin actividades programadas" />;

  return (
    <Flex vertical gap={4}>
      {activities.map((act) => {
        const overdue = dayjs(act.scheduledFor).isBefore(dayjs());
        return (
          <div
            key={`${act.module}-${act.entityId}`}
            style={{
              background: NOTE_BG,
              border: `1px solid ${NOTE_BORDER}`,
              borderRadius: 4,
              padding: "0 8px",
              cursor: "pointer",
            }}
            onClick={() => onNavigate(act.module, act.entityId)}
          >
            <Flex align="flex-start" gap={8} style={{ padding: "6px 0" }}>
              {/* Módulo + entidad */}
              <Flex vertical gap={2} style={{ width: 140, flexShrink: 0 }}>
                <Flex align="center" gap={4}>
                  <span style={{ color: "rgba(0,0,0,0.45)", display: "inline-flex", alignItems: "center" }}>
                    {ACTIVITY_MODULE[act.module]?.icon}
                  </span>
                  <Text style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", lineHeight: "16px" }}>
                    {ACTIVITY_MODULE[act.module]?.label ?? act.module}
                  </Text>
                </Flex>
                <Text strong style={{ color: BRAND, fontSize: 13, lineHeight: "18px" }} ellipsis>
                  {act.label}
                </Text>
              </Flex>
              {/* Contenido */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text italic style={{ fontSize: 12, lineHeight: "20px" }}>
                  {act.content ? renderNoteContent(act.content) : act.label}
                </Text>
              </div>
              {/* Hora */}
              <Text
                style={{
                  fontSize: 12,
                  color: overdue ? "#ff4d4f" : "rgba(0,0,0,0.45)",
                  whiteSpace: "nowrap",
                  alignSelf: "center",
                  flexShrink: 0,
                }}
              >
                {dayjs(act.scheduledFor).format("HH:mm")} hs
              </Text>
            </Flex>
          </div>
        );
      })}
    </Flex>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

export const ParaHacerAhora = ({
  operatorId,
}: {
  operatorId: string | null;
  period?: string;
}) => {
  const { data: user } = useGetIdentity<User>();
  const { selectedStores } = useSelectedStores();
  const operatorContactIds = useOperatorContacts();
  const go = useGo();

  const sellerCodes = selectedStores.map((s) => s.code);

  const [conversations, setConversations] = useState<ConvItem[]>([]);
  const [errorConvs, setErrorConvs] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(false);

  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [errorActs, setErrorActs] = useState(false);
  const [loadingActs, setLoadingActs] = useState(false);

  // ── Conversaciones abiertas ────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    // El servicio de conversaciones puede no estar configurado en este entorno. No es un error:
    // es una mitad del widget que no aplica, y la otra tiene que funcionar igual.
    if (!isConversationsConfigured()) {
      setConversations([]);
      return;
    }
    setLoadingConvs(true);
    setErrorConvs(false);
    const userId = user!.id;
    conversationsApi
      .listConversations({
        status: "open",
        limit: 20,
        ...(operatorId && { participantUserId: Number(operatorId) }),
        ...(sellerCodes.length === 1 && { storeCode: sellerCodes[0] }),
      })
      .then((res) => setConversations(((res as any).items ?? res ?? []) as ConvItem[]))
      .catch(() => {
        setConversations([]);
        setErrorConvs(true);
      })
      .finally(() => setLoadingConvs(false));
  }, [user?.id, operatorId, sellerCodes.join(",")]);

  // ── Próximas actividades ───────────────────────────────────────────────────
  //
  // Se pide la agenda del operador de una: `GET /history/notes/agenda` devuelve sus actividades
  // programadas de TODOS los módulos —contactos, presupuestos, pedidos y conversaciones—, ordenadas
  // por fecha y **con las vencidas primero**.
  //
  // Antes esto armaba una lista de entidades con los primeros 50 presupuestos y 50 pedidos del
  // listado y preguntaba por ésas. Tenía tres agujeros (QA #273.13): una actividad en el
  // presupuesto 51 no aparecía nunca, las conversaciones no se consultaban, y se filtraba a lo
  // agendado para hoy — así que lo vencido, que es lo que más urge, quedaba afuera.
  //
  // Sigue al selector de store del header (xcons-docs#296): con un store elegido se piden sólo
  // sus actividades, de todos sus operadores; sin store, todo lo que el usuario puede ver. Antes
  // ignoraba el selector y mostraba siempre todo el alcance.
  useEffect(() => {
    if (!user?.id) return;
    let vivo = true;
    setLoadingActs(true);
    setErrorActs(false);

    historyNotesApi
      .agenda(20, sellerCodes)
      .then((filas) => {
        if (!vivo) return;
        setActivities(
          filas.map((a) => ({
            module: a.module,
            entityId: a.entityId,
            // El listado no resuelve nombres: para presupuestos y pedidos el número ES la
            // referencia, y para contactos y conversaciones alcanza para navegar.
            label: `#${a.entityId}`,
            scheduledFor: a.scheduledFor,
            content: a.content,
            authorName: a.authorName,
          })),
        );
      })
      .catch(() => {
        if (!vivo) return;
        // La Postgres del historial es una conexión OPCIONAL de la API (`app.module.ts`). Si no
        // responde, esta mitad del widget queda vacía y el resto de la home sigue andando.
        setActivities([]);
        setErrorActs(true);
      })
      .finally(() => vivo && setLoadingActs(false));

    return () => {
      vivo = false;
    };
  }, [user?.id, sellerCodes.join(",")]);

  const handleNavigate = (module: HistoryModule | string, entityId: string) => {
    if (module === "conversation") {
      window.open(`/conversations?open=${entityId}`, "_blank");
      return;
    }
    const routeMap: Record<string, string> = {
      quote:   `/quotes/${entityId}`,
      order:   `/orders/${entityId}`,
      contact: `/contacts/${entityId}`,
    };
    const route = routeMap[module];
    if (route) go({ to: route, type: "push" });
  };

  const cardHeaderStyle = {
    background: "rgba(0,0,0,0.02)",
    borderBottom: "1px solid #f0f0f0",
    padding: "0 16px",
    minHeight: 40,
  };

  return (
    <Flex gap={16} align="flex-start">
      {/* Conversaciones abiertas — 400px = mismo alto que MisClientes */}
      <Card
        size="small"
        style={{ flex: 1, minWidth: 0, overflow: "hidden", height: 400 }}
        styles={{ header: cardHeaderStyle, body: { padding: 0 } }}
        title={
          <SectionHeader
            icon={<MessageOutlined style={{ fontSize: 16, color: "#15744b" }} />}
            label="Conversaciones abiertas"
            count={conversations.length}
            loading={loadingConvs}
            onLabelClick={() => go({ to: "/conversations?status=open", type: "push" })}
          />
        }
      >
        {/* 400px card - 40px header = 360px scroll area */}
        <div style={{ height: 360, overflowY: "auto", padding: "0 16px" }}>
          <ConversacionesAbiertas conversations={conversations} loading={loadingConvs} error={errorConvs} />
        </div>
      </Card>

      {/* Próximas actividades — 400px = mismo alto que MisClientes */}
      <Card
        size="small"
        style={{ flex: 1, minWidth: 0, overflow: "hidden", height: 400 }}
        styles={{ header: cardHeaderStyle, body: { padding: 0 } }}
        title={
          <SectionHeader
            icon={<CalendarOutlined style={{ fontSize: 16, color: "rgba(0,0,0,0.85)" }} />}
            label="Próximas actividades"
            count={activities.length}
            loading={loadingActs}
          />
        }
      >
        {/* 400px card - 40px header = 360px scroll area */}
        <div style={{ height: 360, overflowY: "auto", padding: "8px 16px 12px" }}>
          <ProximasActividades
            activities={activities}
            loading={loadingActs}
            error={errorActs}
            onNavigate={handleNavigate}
          />
        </div>
      </Card>
    </Flex>
  );
};
